import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaBell, FaUserCircle, FaBars } from "react-icons/fa";
import Sidebar from "./Components/Sidebar";
import MyProfile from "./MyProfile";
import "./App.css";
import axios from "axios";
import API_BASE_URL from "./apiBaseUrl";
import {
  buildFleetNotifications,
  fetchFleetNotificationInputs,
  notificationBadgeCount,
} from "./lib/fleetNotifications";

const Navbar = () => {
  const [showProfile, setShowProfile] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const [notificationCount, setNotificationCount] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setIsSidebarOpen(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownOpen && !e.target.closest(".profile-section-wrap")) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [dropdownOpen]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchFleetNotificationInputs(axios, API_BASE_URL);
        if (cancelled) return;
        const alerts = buildFleetNotifications(data);
        setNotificationCount(notificationBadgeCount(alerts));
      } catch {
        if (!cancelled) setNotificationCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Close sidebar when tapping outside on mobile
  useEffect(() => {
    if (!isMobile) return;
    const handleOutside = (e) => {
      if (
        isSidebarOpen &&
        !e.target.closest(".sendo-sidebar") &&
        !e.target.closest(".sendo-hamburger")
      ) {
        setIsSidebarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [isMobile, isSidebarOpen]);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  const handleLogout = async () => {
    try {
      const response = await fetch(API_BASE_URL + "/api/logout", {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        localStorage.setItem("isAuthenticated", "false");
        navigate("/login");
      }
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const styles = {
    navbar: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      height: "70px",
      backgroundColor: "#111",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0 16px",
      zIndex: 1000,
      boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
    },
    left: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    hamburger: {
      color: "white",
      fontSize: "22px",
      cursor: "pointer",
      padding: "6px",
      borderRadius: 4,
      display: "flex",
      alignItems: "center",
    },
    logo: {
      height: "38px",
      objectFit: "contain",
      cursor: "pointer",
    },
    rightSection: {
      display: "flex",
      alignItems: "center",
      gap: "18px",
    },
    notifWrap: {
      position: "relative",
      cursor: "pointer",
      color: "white",
      display: "flex",
      alignItems: "center",
    },
    notifBadge: {
      position: "absolute",
      top: "-6px",
      right: "-8px",
      backgroundColor: "#FFC107",
      color: "#000",
      borderRadius: "50%",
      width: "18px",
      height: "18px",
      fontSize: "11px",
      fontWeight: "bold",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    profileSection: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      position: "relative",
      color: "white",
    },
    username: {
      fontSize: "13px",
      fontWeight: "bold",
      color: "#FFC107",
      whiteSpace: "nowrap",
      display: isMobile ? "none" : "block",
    },
    dropdownMenu: {
      position: "absolute",
      top: "44px",
      right: 0,
      backgroundColor: "#222",
      border: "1px solid #444",
      borderRadius: "6px",
      minWidth: "160px",
      zIndex: 1100,
      boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
      overflow: "hidden",
    },
    dropdownBtn: {
      display: "block",
      width: "100%",
      padding: "12px 16px",
      backgroundColor: "transparent",
      color: "white",
      border: "none",
      textAlign: "left",
      cursor: "pointer",
      fontSize: "14px",
      borderBottom: "1px solid #333",
    },
    // Sidebar overlay for mobile (dim background)
    overlay: {
      display: isMobile && isSidebarOpen ? "block" : "none",
      position: "fixed",
      inset: 0,
      top: "70px",
      backgroundColor: "rgba(0,0,0,0.45)",
      zIndex: 850,
    },
  };

  return (
    <div>
      <nav style={styles.navbar}>
        {/* Left: Hamburger + Logo */}
        <div style={styles.left}>
          <div
            className="sendo-hamburger"
            style={styles.hamburger}
            onClick={toggleSidebar}
            aria-label="Toggle menu"
          >
            <FaBars />
          </div>
          <img
            src="https://sendonow.com/Images/Logo.png"
            alt="Sendo Logo"
            style={styles.logo}
            onClick={toggleSidebar}
          />
        </div>

        {/* Right: Notification (desktop only) + Profile */}
        <div style={styles.rightSection}>
          {/* Bell hidden on mobile */}
          {!isMobile && (
            <div
              style={styles.notifWrap}
              onClick={() => navigate("/notification")}
            >
              <FaBell size={20} />
              {notificationCount > 0 && (
                <span style={styles.notifBadge}>{notificationCount}</span>
              )}
            </div>
          )}

          <div
            className="profile-section-wrap"
            style={styles.profileSection}
            onClick={(e) => { e.stopPropagation(); setDropdownOpen((p) => !p); }}
          >
            <FaUserCircle size={28} color="#FFC107" />
            <span style={styles.username}>OMEG GLOBAL LOGISTICS</span>

            {dropdownOpen && (
              <div style={styles.dropdownMenu}>
                <button
                  style={styles.dropdownBtn}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = "#FFC107")}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowProfile(true);
                    setDropdownOpen(false);
                  }}
                >
                  👤 My Profile
                </button>
                <button
                  style={{ ...styles.dropdownBtn, borderBottom: "none" }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = "#c62828")}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                  onClick={handleLogout}
                >
                  🚪 Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile overlay — tap outside to close sidebar */}
      <div style={styles.overlay} onClick={() => setIsSidebarOpen(false)} />

      {/* Sidebar — wrap with class for outside-click detection */}
      <div className="sendo-sidebar">
        <Sidebar
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      </div>

      {showProfile && <MyProfile />}
    </div>
  );
};

export default Navbar;
